// TypeScript interfaces matching Supabase database tables

export type OrderStatus =
	| 'DRAFT'
	| 'IN_REVIEW'
	| 'PENDING_PAYMENT'
	| 'PAID'
	| 'FULFILLING'
	| 'SHIPPED'
	| 'DELIVERED'
	| 'CANCELLED';

export type PaymentStatus =
	| 'PENDING'
	| 'PROCESSING'
	| 'SUCCEEDED'
	| 'FAILED'
	| 'CANCELLED'
	| 'REFUNDED';

export type ProdigiStatus =
	| 'DRAFT'
	| 'AWAITING_PAYMENT'
	| 'IN_PROGRESS'
	| 'COMPLETE'
	| 'CANCELLED';

export type MessageDirection = 'INBOUND' | 'OUTBOUND';

export type MessageType =
	| 'TEXT'
	| 'IMAGE'
	| 'AUDIO'
	| 'DOCUMENT'
	| 'LOCATION'
	| 'INTERACTIVE';

export type ToolEventType = 'CALL' | 'RESULT';

export type SlotSource =
	| 'USER'
	| 'LLM_EXTRACTION'
	| 'SYSTEM'
	| 'API'
	| 'COPIED';

export type AssetType =
	| 'USER_UPLOAD'
	| 'FRONT_PRINT'
	| 'BACK_PRINT'
	| 'PREVIEW'
	| 'GENERATED';

export interface User {
	id: string;
	phone_number: string;
	display_name: string | null;
	email: string | null;
	stripe_customer_id: string | null;
	created_at: string;
	updated_at: string;
}

export interface Order {
	id: string;
	user_id: string;
	status: OrderStatus;
	created_at: string;
	updated_at: string;
	// Joined
	user?: User;
	slots?: OrderSlot[];
}

export interface OrderSlot {
	id: string;
	order_id: string;
	slot_name: string;
	value: Record<string, unknown> | null;
	confirmed: boolean;
	confidence: number | null;
	source: SlotSource;
	updated_at: string;
}

export interface ConversationMessage {
	id: string;
	user_id: string;
	direction: MessageDirection;
	message_type: MessageType;
	content: string | null;
	media_asset_id: string | null;
	kapso_message_id: string | null;
	message_metadata: Record<string, unknown> | null;
	created_at: string;
}

export interface ToolEvent {
	id: string;
	user_id: string;
	event_type: ToolEventType;
	tool_call_id: string;
	tool_name: string;
	payload: Record<string, unknown>;
	conversation_message_id: string | null;
	created_at: string;
}

export interface Asset {
	id: string;
	user_id: string;
	order_id: string | null;
	asset_type: AssetType;
	storage_key: string;
	mime_type: string;
	original_filename: string | null;
	created_at: string;
}

export interface PaymentIntent {
	id: string;
	order_id: string;
	stripe_payment_intent_id: string;
	amount: number;
	currency: string;
	status: PaymentStatus;
	checkout_url: string | null;
	stripe_session_id: string | null;
	created_at: string;
	updated_at: string;
}

export interface ProdigiOrder {
	id: string;
	order_id: string;
	prodigi_order_id: string;
	status: ProdigiStatus;
	tracking_number: string | null;
	tracking_url: string | null;
	carrier: string | null;
	prodigi_response: Record<string, unknown> | null;
	status_details: Record<string, unknown> | null;
	issues: unknown[] | null;
	created_at: string;
	updated_at: string;
}

export type LobStatus =
	| 'created' | 'rendered' | 'billed' | 'mailed'
	| 'in_transit' | 'in_local_area' | 'processed_for_delivery'
	| 'delivered' | 're_routed' | 'returned_to_sender'
	| 'failed' | 'rejected' | 'deleted' | 'cancelled';
export interface LobOrder {
	id: string;
	order_id: string;
	lob_postcard_id: string;
	status: LobStatus;
	expected_delivery_date: string | null;
	carrier: string | null;
	lob_response: Record<string, unknown> | null;
	tracking_events: unknown[] | null;
	created_at: string;
	updated_at: string;
}

export interface ConversationSummary {
	id: string;
	user_id: string;
	version: number;
	content: string;
	messages_compacted_until: string;
	message_count_compacted: number;
	created_at: string;
}

// Timeline item for chat view - union of messages and tool events
export type TimelineItem =
	| { kind: 'message'; data: ConversationMessage }
	| { kind: 'tool_call'; data: ToolEvent; result?: ToolEvent }
	| { kind: 'tool_result'; data: ToolEvent };

// Required slot names
export const REQUIRED_SLOTS = [
	'front_image_asset_id',
	'message_text',
	'recipient_name',
	'recipient_address_validated',
	'product_variant',
	'price_amount',
	'currency',
	'print_assets_ready',
] as const;

export type CodeStatus = 'active' | 'paused' | 'expired';
export type BenefitType = 'none' | 'percent' | 'fixed_amount';
export type BenefitCurrency = 'credit' | 'cash' | 'product';
export type LedgerEntryType = 'GRANT' | 'SPEND' | 'DEPOSIT' | 'WITHDRAWAL';

export interface Code {
	id: string;
	code: string;
	status: CodeStatus;
	owner_id: string | null;
	starts_at: string | null;
	ends_at: string | null;
	new_users_only: boolean;
	order_max_total_uses: number | null;
	order_max_uses_per_user: number | null;
	order_max_unique_users: number | null;
	owner_max_total_payout_uses: number | null;
	owner_max_payout_uses_per_user: number | null;
	owner_max_unique_users: number | null;
	redeemer_type: BenefitType;
	redeemer_currency: BenefitCurrency | null;
	redeemer_value: number | null;
	owner_type: BenefitType;
	owner_currency: BenefitCurrency | null;
	owner_value: number | null;
	metadata_json: Record<string, unknown> | null;
	created_at: string;
	updated_at: string;
}

export interface CodeRedemption {
	id: string;
	code_id: string;
	user_id: string;
	order_id: string | null;
	created_at: string;
	first_applied_at: string;
	last_applied_at: string;
	apply_count: number;
}

export interface LedgerTransaction {
	id: string;
	order_id: string | null;
	user_id: string;
	code_id: string | null;
	entry_type: LedgerEntryType;
	currency: BenefitCurrency;
	amount: number;
	reason: string;
	created_at: string;
}

// Health monitoring
export type HealthStatus = 'healthy' | 'degraded' | 'down';

export interface HealthCheck {
	id: string;
	service_name: string;
	status: HealthStatus;
	response_time_ms: number | null;
	status_code: number | null;
	error_message: string | null;
	checked_at: string;
}
